import { Timestamp } from 'firebase/firestore';
import { balanceOf, isRecord, metres, ms, splitAddress, wall, type Doc, type HandIn, type Spot, type Stop } from '@day';
import { current, keyOf, routeTitle, type Look, type Model, type Person } from '../data/model';
import { pendCleared, pendHandIn, pendRoute, pendSentStop, pendWaiting, pendZone, pending, unpendCleared, unpendHandIn, unpendRoute, unpendRouteIf, unpendSentStop, unpendWaiting, unpendZone } from '../data/pending';
import { zoneFields, zoneProblem, type Spot as ZoneSpot } from '../data/zones';
import { data } from '../data/live';
import { CLOCK_MARGIN_MS, GOOGLE_MS, deadlineOn, problemOf, readyToPlan, spotOf, waitingFields, type Source, type Waiting } from '../data/waiting';
import { copiedStops, placedAgain } from '../data/copyRoute';
import { findAddress, findPlaceId, inTurn } from '../data/find';
import { LookupError, type GooglePlace } from '../data/maps';
import { atPlaces, nearestOrder, optimizationJson, sortStops, straight, timeRoute, type Hint, type Timed } from '../data/routing';
import { combine, DEFAULT_LOAD_S, defaultCapacity, split, type RunSpanJson, type RunStop } from '../data/runs';
import { decode, encode } from '../data/polyline';
import { googlePlace, lookup } from '../data/maps';
import * as write from '../data/write';
import { hold, now, say } from './hold';
import { stopsBucket, track } from '../data/analytics';
import { backToDay, openRoute, planWith, setShare, ui } from './ui';
import { sharedNow, zoneWarning } from './sharing';
import { dayWords, money, plural, streetOf, time, vehicleWords, whatsAppNumber } from './words';
import { createStore } from './store';
import { toast } from '../ui/Overlays';

/**
 * What the owner does from the control room, writing the records the phone writes (data/write.ts). Whatever reaches a
 * courier or a customer waits 5 seconds with Undo (hold.ts). A route a courier has started is theirs: the rules let an
 * owner change a route only until it starts, so its order and its stops change on the courier's phone, and these
 * actions say so rather than offer it.
 */

const model = (): Model => {
  const m = current.m;
  if (!m?.business) throw new Error('The control room isn’t loaded yet');
  return m;
};

const first = (m: Model, uid: string) => m.person(uid)?.first ?? 'the courier';

/**
 * Why a route's order and stops can't change from here, or null when they can: only a route waiting to start. A started
 * one is the courier's (the rules); a declined one goes to someone else or back, as on the phone, because any change
 * would quietly hand it back to the courier who couldn't take it.
 */
export function cantChange(m: Model, raw: Doc | null | undefined): string | null {
  if (!raw) return 'That route has gone';
  if (raw.status === 'sent') return null;
  if (raw.status === 'declined') return `${first(m, String(raw.riderId ?? ''))} can’t take it: send it to someone else or take it back first`;
  return 'That route has started, so its order is the courier’s now';
}

// ── Stops as the phone stores them on a sent route ──────────────────────

/** Dart's `toIso8601String()` of a phone's local time: no zone, as a sent route's deliverBy is stored. */
export function localIso(t: number, zone: string): string {
  const w = wall(t, zone);
  const p = (n: number, k = 2) => String(n).padStart(k, '0');
  return `${w.year}-${p(w.month)}-${p(w.day)}T${p(w.hour)}:${p(w.minute)}:${p(w.second)}.000`;
}

/** A stop as `storedStop` writes it (DeliveryStop.toJson): every key, nulls included. */
export function storedStop(o: Doc & { id: string; address: string; lat: number; lng: number }): Doc {
  return {
    writtenAddress: null,
    placeId: null,
    houseNumber: null,
    recipientInitial: null,
    recipientName: null,
    recipientPhone: null,
    status: 'pending',
    type: 'delivery',
    pickupId: null,
    retryOf: null,
    sentStopId: null,
    orderHint: 'auto',
    precision: 'exact',
    accessInstructions: null,
    packageCount: 1,
    size: 'regular',
    runHint: null,
    dwellMinutes: null,
    breakMinutes: null,
    deliverBy: null,
    parcelType: null,
    podPhotoUrl: null,
    podPhotoUrls: null,
    courierNote: null,
    completedAt: null,
    failureReason: null,
    collectOrder: null,
    collectFee: null,
    currency: null,
    paidHow: null,
    paidAmount: null,
    paidToCourier: null,
    notes: null,
    ...o,
  };
}

/**
 * A stop from any route as a sent stop or a new route's stop: a sent route's stops are stored that way already; a
 * running route's record has the stop and its working copy has the customer (RouteHistoryService._details).
 */
export function stopForSending(m: Model, routeId: string, stopId: string): Doc | null {
  const raw = m.raw(routeId);
  if (!raw) return null;
  const stops = Array.isArray(raw.stops) ? (raw.stops as Doc[]) : [];
  const rec = stops.find((s) => s.id === stopId);
  if (!rec) return null;
  if (raw.status === 'sent' || raw.status === 'declined') return { ...rec };
  const lat = typeof rec.lat === 'number' ? rec.lat : (rec.doneLat as number | undefined);
  const lng = typeof rec.lng === 'number' ? rec.lng : (rec.doneLng as number | undefined);
  if (lat == null || lng == null) return null;
  return storedFromRecord(m, raw, rec, lat, lng);
}

/** A stop as `storedStop` writes it, from a started route's record and its working copy, at the position given. */
function storedFromRecord(m: Model, raw: Doc, rec: Doc, lat: number, lng: number): Doc {
  const stopId = String(rec.id);
  const resume = isRecord(raw.resume) ? raw.resume : {};
  const d = (isRecord(resume.details) && isRecord(resume.details[stopId]) ? resume.details[stopId] : {}) as Doc;
  const by = ms(rec.deliverBy, m.zone);
  const pick = <T>(k: string) => (d[k] ?? rec[k] ?? null) as T | null;
  return storedStop({
    id: String(rec.id),
    address: String(rec.address),
    writtenAddress: (rec.writtenAddress as string | undefined) ?? null,
    placeId: (rec.placeId as string | undefined) ?? null,
    houseNumber: (rec.houseNumber as string | undefined) ?? null,
    lat,
    lng,
    type: rec.type === 'pickup' ? 'pickup' : 'delivery',
    precision: typeof rec.lat === 'number' ? ((rec.precision as string | undefined) ?? 'exact') : 'low',
    packageCount: (rec.packageCount as number | undefined) ?? 1,
    size: (rec.size as string | undefined) ?? 'regular',
    deliverBy: by != null ? localIso(by, m.zone) : null,
    recipientName: pick('recipientName'),
    recipientPhone: pick('recipientPhone'),
    recipientInitial: pick('recipientInitial'),
    accessInstructions: pick('accessInstructions'),
    notes: pick('notes'),
    dwellMinutes: pick('dwellMinutes'),
    breakMinutes: pick('breakMinutes'),
    collectOrder: pick('collectOrder'),
    collectFee: pick('collectFee'),
    currency: pick('currency'),
  });
}

// ── Timing a route not started ───────────────────────────────────────────

const pointOf = (s: Doc) => ({ id: String(s.id), lat: Number(s.lat), lng: Number(s.lng) });
const placed = (s: Doc) => typeof s.lat === 'number' && typeof s.lng === 'number';

/** A pickup goes before the delivery it's for (PickupOrder): after a reversal, each pickup moves just ahead of it. */
export function pickupsFirst(stops: Doc[]): Doc[] {
  const out = [...stops];
  for (let i = 0; i < out.length; i++) {
    const pid = out[i].pickupId;
    if (typeof pid !== 'string') continue;
    const j = out.findIndex((s) => s.id === pid);
    if (j > i) {
      const [p] = out.splice(j, 1);
      out.splice(i, 0, p);
      i++;
    }
  }
  return out;
}

/** The stops in [order] and their times: straight lines now, Google's (through Routr's server) when sent. */
function retimed(raw: Doc, stops: Doc[], t: Timed): Doc {
  const start = isRecord(raw.start) ? raw.start : null;
  return { ...raw, stops, optimization: optimizationJson(t, !!start) };
}

const startOf = (raw: Doc): Spot | null => (isRecord(raw.start) && typeof raw.start.lat === 'number' ? { lat: raw.start.lat as number, lng: raw.start.lng as number } : null);

async function timedAgain(raw: Doc, stops: Doc[]): Promise<Doc> {
  const ok = stops.filter(placed);
  if (ok.length !== stops.length) return retimed(raw, stops, straight(startOf(raw), ok.map(pointOf)));
  return retimed(raw, stops, await timeRoute(startOf(raw), stops.map(pointOf), { optimise: false }));
}

/** Changes a route not started: shown at once, written after the hold with Google's times. */
function changeSent(routeId: string, stops: Doc[], msg: string, sent: string, how: 'drag' | 'reverse' | 'sort', timed?: Timed) {
  const m = model();
  const raw = m.raw(routeId);
  const why = cantChange(m, raw);
  if (why || !raw) return toast(why ?? 'That route has gone');
  const shown = retimed(raw, stops, timed ?? straight(startOf(raw), stops.filter(placed).map(pointOf)));
  pendRoute(routeId, shown);
  hold(msg, {
    send: async () => {
      const next = timed ? shown : await timedAgain(raw, stops);
      await write.reorderSent(routeId, next.stops as Doc[], next.optimization as Doc);
      // A newer change to this route stays shown until it's written too.
      unpendRouteIf(routeId, shown);
      track('live_action', { action: how });
    },
    undo: () => unpendRouteIf(routeId, shown),
    sent,
  });
}

/** Drag: a stop on a route not started, in a new place. */
export function moveWithin(routeId: string, stopId: string, to: number) {
  const m = model();
  const raw = m.raw(routeId);
  const r = m.route(routeId);
  if (!raw || !r) return;
  const stops = [...(raw.stops as Doc[])];
  const from = stops.findIndex((s) => s.id === stopId);
  if (from < 0 || from === to) return;
  const [s] = stops.splice(from, 1);
  stops.splice(Math.max(0, Math.min(to, stops.length)), 0, s);
  const fixed = pickupsFirst(stops);
  const n = fixed.findIndex((x) => x.id === stopId) + 1;
  changeSent(routeId, fixed, `${String(s.address).split(',')[0]} is now stop ${n} · ${first(m, r.riderId)} sees the new order`, `${routeTitle(r, m.zone)}: the new order is sent`, 'drag');
}

/** Reverse: the same stops the other way round, timed again; a pickup still before its delivery. */
export function reverse(routeId: string) {
  const m = model();
  const raw = m.raw(routeId);
  const r = m.route(routeId);
  if (!raw || !r) return;
  const stops = pickupsFirst([...(raw.stops as Doc[])].reverse());
  changeSent(routeId, stops, `${routeTitle(r, m.zone)} the other way round · timed again`, `${routeTitle(r, m.zone)} reversed and sent to ${first(m, r.riderId)}`, 'reverse');
}

/**
 * Sort again, until the route starts (then it's the courier's): Google chooses the order through Routr's server, stops
 * pinned First and Last keep their places, a pickup stays before its delivery. Sent to the courier after 5 s with Undo.
 */
export async function sortSent(routeId: string) {
  const m = model();
  const raw = m.raw(routeId);
  const r = m.route(routeId);
  const why = cantChange(m, raw);
  if (why || !raw || !r) return toast(why ?? 'That route has gone');
  const stops = raw.stops as Doc[];
  if (stops.length < 2) return;
  if (!stops.every(placed)) return toast('A stop on this route has no position to sort by');
  toast(`Sorting ${routeTitle(r, m.zone)}…`, { ms: 30_000 });
  const back = isRecord(raw.optimization) && raw.optimization.round_trip === true;
  const t = await sortStops(
    startOf(raw),
    stops.map((x) => ({ ...pointOf(x), hint: (x.orderHint as Hint | undefined) ?? 'auto', pickupId: (x.pickupId as string | null | undefined) ?? null })),
    back,
  );
  const byId = new Map(stops.map((x) => [String(x.id), x]));
  if (t.order.join() === stops.map((x) => x.id).join()) return toast(`${routeTitle(r, m.zone)} is in its best order already${t.method === 'haversine' ? ' (by straight lines)' : ''}`);
  changeSent(routeId, t.order.map((id) => byId.get(id)!), `${routeTitle(r, m.zone)} sorted again · ${first(m, r.riderId)} sees the new order`, `${routeTitle(r, m.zone)}: the new order is sent`, 'sort', t);
}

/** Where a stop fits best on a route not started: the gap that adds least, by straight lines. */
export function fitIndex(start: Spot | null, stops: Doc[], x: Spot): number {
  let best = stops.length;
  let cost = Infinity;
  for (let i = 0; i <= stops.length; i++) {
    const a = i === 0 ? start : placed(stops[i - 1]) ? pointOf(stops[i - 1]) : null;
    const b = stops[i] && placed(stops[i]) ? pointOf(stops[i]) : null;
    const c = (a ? metres(a, x) : 0) + (b ? metres(x, b) - (a ? metres(a, b) : 0) : 0);
    if (c < cost) {
      cost = c;
      best = i;
    }
  }
  return best;
}

/** Move to: a stop from one route not started to another, where it fits best. Both couriers see it. */
export function moveStops(keys: string[], toRouteId: string) {
  const m = model();
  const to = m.raw(toRouteId);
  const toR = m.route(toRouteId);
  if (!to || !toR) return toast('Stops can move only to a route waiting to start');
  const toWhy = cantChange(m, to);
  if (toWhy) return toast(toWhy);
  const byRoute = new Map<string, string[]>();
  let declined: Doc | null = null;
  for (const k of keys) {
    const [rid, sid] = [k.slice(0, k.indexOf('/')), k.slice(k.indexOf('/') + 1)];
    if (rid === toRouteId) continue;
    const raw = m.raw(rid);
    if (raw?.status === 'declined') declined = raw;
    if (cantChange(m, raw)) continue;
    byRoute.set(rid, [...(byRoute.get(rid) ?? []), sid]);
  }
  if (!byRoute.size) return toast(declined ? cantChange(m, declined)! : keys.length === 1 ? 'That stop can’t move: its route has started or it’s there already' : 'None of those can move: their routes have started');
  let toStops = [...(to.stops as Doc[])];
  const changes: { routeId: string; stops: Doc[]; raw: Doc }[] = [];
  const gone: string[] = [];
  let moved = 0;
  for (const [rid, ids] of byRoute) {
    const raw = m.raw(rid)!;
    const left = (raw.stops as Doc[]).filter((s) => !ids.includes(String(s.id)));
    for (const s of (raw.stops as Doc[]).filter((x) => ids.includes(String(x.id)))) {
      // Two stops can't share an id on one route.
      const taken = new Set(toStops.map((x) => x.id));
      const stop = taken.has(s.id) ? { ...s, id: `${s.id}-${crypto.randomUUID().slice(0, 4)}` } : s;
      const at = placed(stop) ? fitIndex(startOf(to), toStops, pointOf(stop)) : toStops.length;
      toStops.splice(at, 0, stop);
      moved++;
    }
    if (left.length) changes.push({ routeId: rid, stops: left, raw });
    else gone.push(rid);
  }
  toStops = pickupsFirst(toStops);
  const toFirst = first(m, toR.riderId);
  const shown = new Map<string, Doc | null>();
  for (const c of changes) shown.set(c.routeId, retimed(c.raw, c.stops, straight(startOf(c.raw), c.stops.filter(placed).map(pointOf))));
  for (const id of gone) shown.set(id, null);
  shown.set(toRouteId, retimed(to, toStops, straight(startOf(to), toStops.filter(placed).map(pointOf))));
  for (const [id, doc] of shown) pendRoute(id, doc);
  // Each route stays as shown until written, unless a newer change to it is showing by then.
  const undo = () => shown.forEach((doc, id) => unpendRouteIf(id, doc));
  hold(`${plural(moved, 'stop')} to ${toFirst}’s ${routeTitle(toR, m.zone)}${gone.length ? ' · the route it left is taken back' : ''}`, {
    send: async () => {
      const all = [...changes.map(async (c) => ({ routeId: c.routeId, doc: await timedAgain(c.raw, c.stops) })), (async () => ({ routeId: toRouteId, doc: await timedAgain(to, toStops) }))()];
      const timed = await Promise.all(all);
      await write.reorderMany(
        timed.map((t) => ({ routeId: t.routeId, stops: t.doc.stops as Doc[], optimization: t.doc.optimization as Doc })),
        gone,
      );
      undo();
      track('live_action', { action: 'move', stops: stopsBucket(moved) });
    },
    undo,
    sent: `Moved: ${toFirst} has ${plural(toStops.length, 'stop')} now`,
  });
  ui.set({ sel: new Set() });
}

/** What taking [keys] off their routes changes: the routes left shorter, and those left empty (taken back). */
function takeOffPlan(m: Model, keys: string[]) {
  const byRoute = new Map<string, string[]>();
  let declined: Doc | null = null;
  for (const k of keys) {
    const rid = k.slice(0, k.indexOf('/'));
    const raw = m.raw(rid);
    if (raw?.status === 'declined') declined = raw;
    if (cantChange(m, raw)) continue;
    byRoute.set(rid, [...(byRoute.get(rid) ?? []), k.slice(k.indexOf('/') + 1)]);
  }
  const changes: { routeId: string; stops: Doc[]; raw: Doc }[] = [];
  const gone: string[] = [];
  let n = 0;
  for (const [rid, ids] of byRoute) {
    const raw = m.raw(rid)!;
    const left = (raw.stops as Doc[]).filter((s) => !ids.includes(String(s.id)));
    n += (raw.stops as Doc[]).length - left.length;
    if (left.length) changes.push({ routeId: rid, stops: left, raw });
    else gone.push(rid);
  }
  return { changes, gone, n, declined };
}

/** The routes [takeOffPlan] changed, timed again and written (taken back when empty). */
async function writeTakeOff(changes: { routeId: string; stops: Doc[]; raw: Doc }[], gone: string[]) {
  const timed = await Promise.all(changes.map(async (c) => ({ routeId: c.routeId, doc: await timedAgain(c.raw, c.stops) })));
  await write.reorderMany(
    timed.map((t) => ({ routeId: t.routeId, stops: t.doc.stops as Doc[], optimization: t.doc.optimization as Doc })),
    gone,
  );
}

/** Take off: stops off a route waiting to start. The last one off takes the route back. */
export function takeOffStops(keys: string[]) {
  const m = model();
  const { changes, gone, n, declined } = takeOffPlan(m, keys);
  if (!n) return toast(declined ? cantChange(m, declined)! : keys.length === 1 ? 'That route has started: stops come off on the courier’s phone' : 'Those routes have started: stops come off on the couriers’ phones');
  const shown = new Map<string, Doc | null>();
  for (const c of changes) shown.set(c.routeId, retimed(c.raw, c.stops, straight(startOf(c.raw), c.stops.filter(placed).map(pointOf))));
  for (const id of gone) shown.set(id, null);
  for (const [id, doc] of shown) pendRoute(id, doc);
  const undo = () => shown.forEach((doc, id) => unpendRouteIf(id, doc));
  const s = ui.get();
  if (s.view === 'stop' && s.stop && keys.includes(s.stop)) ui.set({ view: gone.includes(s.route ?? '') ? 'day' : 'route', stop: null });
  if (s.rec?.type === 'stop' && keys.includes(s.rec.id)) ui.set({ rec: null });
  hold(`${plural(n, 'stop')} taken off${gone.length ? ` · ${gone.length === 1 ? 'its route is' : 'their routes are'} taken back` : ''}`, {
    send: async () => {
      await writeTakeOff(changes, gone);
      undo();
      track('live_action', { action: 'take_off', stops: stopsBucket(n) });
    },
    undo,
  });
  ui.set({ sel: new Set() });
}

// ── Routes not started: give to someone else, take back ─────────────────

export function giveTo(routeId: string, riderId: string) {
  const m = model();
  const raw = m.raw(routeId);
  const r = m.route(routeId);
  if (!raw || !r || (raw.status !== 'sent' && raw.status !== 'declined')) return toast('That route has started: it stays with its courier');
  if (riderId === r.riderId && raw.status === 'sent') return;
  const fresh = write.newRouteRef();
  const was = first(m, r.riderId);
  const to = first(m, riderId);
  const freshDoc = { ...raw, status: 'sent', riderId, sentAt: Date.now(), seenAt: null, orderChangedAt: null, declinedAt: null, declineReason: null, declineNote: null };
  pendRoute(routeId, null);
  pendRoute(fresh.id, freshDoc);
  if (ui.get().route === routeId) ui.set({ route: fresh.id });
  hold(`Giving ${routeTitle(r, m.zone)} to ${to}`, {
    send: async () => {
      await write.sendTo(fresh, { ...raw, id: routeId }, riderId);
      unpendRouteIf(routeId, null);
      unpendRouteIf(fresh.id, freshDoc);
      track('sent_route_reassigned', { declined: raw.status === 'declined', stops: stopsBucket(r.stops.length) });
    },
    undo: () => {
      unpendRouteIf(routeId, null);
      unpendRouteIf(fresh.id, freshDoc);
      if (ui.get().route === fresh.id) ui.set({ route: routeId });
    },
    sent: `${routeTitle(r, m.zone)} sent to ${to}${riderId !== r.riderId ? ` · off ${was}’s list` : ''}`,
  });
}

/**
 * "Swap with…": two routes waiting to start exchange couriers. Each is sent afresh to the other's courier, as "Give it to
 * someone else" sends it, so the server tells them both; in one transaction, so both change hands or neither does.
 */
export function swapRoutes(aId: string, bId: string) {
  const m = model();
  const rawA = m.raw(aId);
  const rawB = m.raw(bId);
  const a = m.route(aId);
  const b = m.route(bId);
  if (!rawA || !rawB || !a || !b || rawA.status !== 'sent' || rawB.status !== 'sent') return toast('One of those routes has started or gone, so nothing was swapped');
  const freshA = write.newRouteRef();
  const freshB = write.newRouteRef();
  const shown = (raw: Doc, riderId: string) => ({ ...raw, status: 'sent', riderId, sentAt: Date.now(), seenAt: null, orderChangedAt: null, declinedAt: null, declineReason: null, declineNote: null });
  const docA = shown(rawA, b.riderId);
  const docB = shown(rawB, a.riderId);
  pendRoute(aId, null);
  pendRoute(bId, null);
  pendRoute(freshA.id, docA);
  pendRoute(freshB.id, docB);
  const open = ui.get().route;
  if (open === aId) ui.set({ route: freshA.id });
  if (open === bId) ui.set({ route: freshB.id });
  const clear = () => {
    unpendRouteIf(aId, null);
    unpendRouteIf(bId, null);
    unpendRouteIf(freshA.id, docA);
    unpendRouteIf(freshB.id, docB);
  };
  const toA = first(m, a.riderId);
  const toB = first(m, b.riderId);
  const words = `${routeTitle(a, m.zone)} to ${toB}, ${routeTitle(b, m.zone)} to ${toA}`;
  hold(`Swapping: ${words}`, {
    send: async () => {
      await write.swapSent({ id: aId, fresh: freshA, riderId: a.riderId }, { id: bId, fresh: freshB, riderId: b.riderId });
      clear();
      track('sent_routes_swapped', { stops: stopsBucket(a.stops.length + b.stops.length) });
    },
    undo: () => {
      clear();
      const shownNow = ui.get().route;
      if (shownNow === freshA.id) ui.set({ route: aId });
      if (shownNow === freshB.id) ui.set({ route: bId });
    },
    failed: (e) => (e instanceof write.RouteChanged ? 'Nothing was swapped: one of those routes has just started, changed hands or been taken back.' : 'Nothing was swapped. Check your connection and try again.'),
    sent: `Swapped: ${words} · both are told`,
  });
}

export function takeBackRoute(routeId: string) {
  const m = model();
  const raw = m.raw(routeId);
  const r = m.route(routeId);
  if (!raw || !r || (raw.status !== 'sent' && raw.status !== 'declined')) return toast('That route has started: only its courier can end it');
  pendRoute(routeId, null);
  const s = ui.get();
  if (s.route === routeId) backToDay();
  const restore = restoreWaiting(m, raw);
  if (restore.length) pendWaiting(restore.map((x) => x.id), (id) => restore.find((x) => x.id === id)!.fields);
  hold(`${routeTitle(r, m.zone)} taken back · off ${first(m, r.riderId)}’s list${restore.length ? ` · ${plural(restore.length, 'stop')} back in Waiting` : ''}`, {
    send: async () => {
      await write.takeBack(routeId, { businessId: m.business!.id, items: restore });
      unpendWaiting(restore.map((x) => x.id));
      unpendRouteIf(routeId, null);
      track('sent_route_taken_back', { declined: raw.status === 'declined' });
    },
    undo: () => {
      unpendRouteIf(routeId, null);
      unpendWaiting(restore.map((x) => x.id));
    },
  });
}

/**
 * The waiting records of a sent route's stops that came from Waiting (its `fromWaiting`), to put back when it's taken
 * back: only stops still on it, not already waiting again, whose 28 days aren't over. Google's position comes back only
 * while the route may keep it; a customer's pin stays on the stop.
 */
export function restoreWaiting(m: Model, raw: Doc): { id: string; fields: Doc }[] {
  return restoreFrom(m, isRecord(raw.fromWaiting) ? raw.fromWaiting : {}, Array.isArray(raw.stops) ? (raw.stops as Doc[]) : [], ms(raw.googleUntil));
}

/**
 * The waiting records of [stops] that came from Waiting ([from]: each one's source, sheet, day, deliver-by time, its own
 * `expiresAt`, and whether it's a pin). Google's position comes back only until [until]; without one, it's looked up again
 * when it's planned.
 */
function restoreFrom(m: Model, from: Doc, stops: Doc[], until: number | null): { id: string; fields: Doc }[] {
  const t = Date.now();
  return Object.entries(from).flatMap(([id, meta]) => {
    const x = stops.find((y) => y.id === id);
    const mm = isRecord(meta) ? meta : {};
    const expires = typeof mm.expiresAt === 'number' ? mm.expiresAt : null;
    if (!x || !expires || expires <= t || data.get().waiting.has(id)) return [];
    const { lat, lng, ...rest } = x;
    const pin = mm.pin === true;
    const fields: Doc = {
      ...waitingFields({ businessId: m.business!.id, stop: { ...rest, id, address: String(x.address), lat: pin ? lat : null, lng: pin ? lng : null, status: 'pending', deliverBy: null } as Doc & { id: string; address: string }, source: mm.source === 'sheet' ? 'sheet' : 'typed', sheet: isRecord(mm.sheet) ? (mm.sheet as { file: string; row: number }) : null, day: typeof mm.day === 'string' ? mm.day : null, byTime: typeof mm.byTime === 'string' ? mm.byTime : null, now: t }),
      expiresAt: Timestamp.fromMillis(expires),
    };
    if (!pin && typeof lat === 'number' && typeof lng === 'number' && until != null && until > t) Object.assign(fields, { google: { lat, lng }, googleUntil: Timestamp.fromMillis(until) });
    return [{ id, fields }];
  });
}

// ── A new route ──────────────────────────────────────────────────────────

/** A stop on a route being planned. */
export interface PlanStop {
  id: string;
  address: string;
  writtenAddress?: string;
  area: string;
  lat: number;
  lng: number;
  placeId?: string;
  houseNumber?: string;
  /** 'low': Google placed only the street or the area, so the courier is asked to confirm with the customer. */
  precision?: 'exact' | 'low';
  name?: string;
  phone?: string;
  notes?: string;
  /** A customer's own pin (not Google's): kept as long as the route. */
  pin?: boolean;
  /** When Google's position must go, for a stop taken from an earlier route. */
  googleUntil?: number | null;
  /** The whole stored stop, for one taken from a route (Try again, another day). */
  from?: Doc;
  /** The stop it came from, to take off a route not started once this is sent. */
  fromKey?: string;
  /** The waiting stop it is (docs/waiting-stops.md): it leaves the list in the same write as the route. */
  waitingId?: string;
  /** First, Auto (the default) or Last in the order (the phone's OrderHint). */
  hint?: Hint;
  /** Its number in the order, typed by the owner (from 1): Sort and Reverse fit the rest round it. In place of a hint. */
  place?: number;
  /** Copied from a route: its customer can still be changed here, since a finished route's record has none. */
  copied?: boolean;
}

export interface Plan {
  stops: PlanStop[];
  to: string | null;
  name: string;
  /** Where it starts: unset, where routes start (the shop); null, wherever the courier is; else a place or pin. */
  start?: { label: string; lat: number; lng: number } | null;
  /** Back to start at the end (the phone's round trip). */
  back?: boolean;
  /** The order the owner chose or Sort made (stop ids); unset, Routr sorts it when it's sent. */
  order?: string[] | null;
  /** That order's times, when they're Google's (or straight lines Sort fell back to). */
  timed?: Timed | null;
  /**
   * Split into runs from where it starts (src/data/runs.ts): [n] null, as many as the courier's vehicle needs; a number,
   * shared out about evenly into that many. Unset: one run.
   */
  runs?: { n: number | null } | null;
  /** "Keep one run" for these stops (their ids, sorted): the offer to split isn't made again for them. */
  keptOne?: string | null;
}

/** The stops of a plan as runs see them: their packages, sizes, pins, deadlines and pickups. */
export const runStopsOf = (plan: Plan): RunStop[] =>
  plan.stops.map((x) => ({
    id: x.id,
    lat: x.lat,
    lng: x.lng,
    packageCount: typeof x.from?.packageCount === 'number' ? x.from.packageCount : 1,
    size: typeof x.from?.size === 'string' ? x.from.size : 'regular',
    orderHint: x.hint ?? (typeof x.from?.orderHint === 'string' ? x.from.orderHint : 'auto'),
    runHint: typeof x.from?.runHint === 'number' ? x.from.runHint : null,
    deliverBy: typeof x.from?.deliverBy === 'string' ? x.from.deliverBy : null,
    pickupId: typeof x.from?.pickupId === 'string' ? x.from.pickupId : null,
  }));

/** What the courier taking [plan] carries (their vehicle's), or null with nobody picked. */
export const capacityOf = (m: Model, plan: Plan) => (plan.to ? defaultCapacity(m.person(plan.to)?.courier.vehicle) : null);

/** The plan's runs from where it starts, or null when it isn't split (or has nowhere to load). */
export function planRuns(m: Model, plan: Plan): { runs: RunStop[][]; depot: Spot } | null {
  const start = planStart(m, plan);
  const cap = capacityOf(m, plan);
  if (!plan.runs || !start || cap == null) return null;
  const runs = split(runStopsOf(plan), start, cap, plan.runs.n);
  return runs.length > 1 ? { runs, depot: start } : null;
}

/**
 * Each run's order and times, from where the courier loads and back there (the last run back only with Back to start), one
 * after another (runs.ts `combine`): Google's through Routr's server when [google], else straight lines.
 */
/** [timeRuns] by straight lines, at once: what the planner shows before Google times it. */
export function runsStraight(runs: RunStop[][], depot: Spot, back: boolean): Timed & { runs: RunSpanJson[] } {
  const parts = runs.map((run, i) => straight(depot, nearestOrder(depot, run.map((x) => ({ id: x.id, lat: x.lat, lng: x.lng }))), undefined, i < runs.length - 1 || back));
  return combine(parts, depot, DEFAULT_LOAD_S);
}

export async function timeRuns(runs: RunStop[][], depot: Spot, back: boolean, google: boolean): Promise<Timed & { runs: RunSpanJson[] }> {
  const parts = await Promise.all(
    runs.map((run, i) => {
      const loop = i < runs.length - 1 || back;
      const pts = run.map((x) => ({ id: x.id, lat: x.lat, lng: x.lng, hint: (x.orderHint as Hint | undefined) ?? 'auto', pickupId: x.pickupId ?? null }));
      return google ? sortStops(depot, pts, loop) : Promise.resolve(straight(depot, nearestOrder(depot, pts), undefined, loop));
    }),
  );
  const c = combine(parts, depot, DEFAULT_LOAD_S, (lines) => encode(lines.flatMap((l) => decode(l))));
  return { ...c, method: c.method };
}

/** Where a new route starts: as set, else where routes start. */
export const planStart = (m: Model, plan: Plan) => (plan.start === undefined ? (m.shop ? { label: m.shop.label, lat: m.shop.lat, lng: m.shop.lng } : null) : plan.start);

/** The plan's stops in the order they'd go: the order chosen, else nearest next from the start; First, Last and numbers kept. */
export function planOrder(m: Model, plan: Plan): PlanStop[] {
  const byId = new Map(plan.stops.map((x) => [x.id, x]));
  const chosen = (plan.order ?? []).map((id) => byId.get(id)).filter((x): x is PlanStop => !!x);
  const rest = plan.stops.filter((x) => !chosen.includes(x));
  let ordered = [...chosen, ...straightOrder(planStart(m, plan), rest)];
  ordered = [...ordered.filter((x) => x.hint === 'first'), ...ordered.filter((x) => x.hint !== 'first' && x.hint !== 'last'), ...ordered.filter((x) => x.hint === 'last')];
  const places = new Map(ordered.filter((x) => x.place != null).map((x) => [x.id, x.place!] as [string, number]));
  ordered = atPlaces(ordered.map((x) => x.id), places).map((id) => byId.get(id)!);
  const pickupOf = new Map(ordered.filter((x) => x.from?.pickupId).map((x) => [x.id, String(x.from!.pickupId)] as [string, string]));
  for (let i = 0; i < ordered.length; i++) {
    const j = ordered.findIndex((y) => y.id === pickupOf.get(ordered[i].id));
    if (j > i) {
      const [p] = ordered.splice(j, 1);
      ordered.splice(i, 0, p);
      i++;
    }
  }
  return ordered;
}

function straightOrder(start: Spot | null, stops: PlanStop[]): PlanStop[] {
  const left = [...stops];
  const out: PlanStop[] = [];
  let at = start;
  while (left.length) {
    let bi = 0;
    if (at) {
      let bd = Infinity;
      left.forEach((x, i) => {
        const d = metres(at!, x);
        if (d < bd) {
          bd = d;
          bi = i;
        }
      });
    }
    const next = left.splice(bi, 1)[0];
    out.push(next);
    at = next;
  }
  return out;
}

/** The plan's times: Sort's when they're for this very order, else straight lines (Google times it as it's sent). */
export function planTimed(m: Model, plan: Plan): Timed {
  // Split into runs: run by run from the load point, with the way back and the loading between them.
  const rp = planRuns(m, plan);
  if (rp) return runsStraight(rp.runs, rp.depot, !!plan.back);
  const ordered = planOrder(m, plan);
  if (plan.timed && plan.timed.order.join() === ordered.map((x) => x.id).join()) return plan.timed;
  return straight(planStart(m, plan), ordered.map((x) => ({ id: x.id, lat: x.lat, lng: x.lng })), undefined, !!plan.back);
}

/** Sort a plan through Google, as the phone's Sort does; its order and times are kept on the plan. */
export async function sortPlan(plan: Plan): Promise<Plan> {
  const m = model();
  const t = await sortStops(
    planStart(m, plan),
    plan.stops.map((x) => ({ id: x.id, lat: x.lat, lng: x.lng, hint: x.hint ?? 'auto', place: x.place, pickupId: (x.from?.pickupId as string | undefined) ?? null })),
    !!plan.back,
  );
  track('route_sorted', { stops: stopsBucket(plan.stops.length), timed: t.method, back_to_start: !!plan.back });
  return { ...plan, order: t.order, timed: t };
}

export function planStopToStored(p: PlanStop): Doc {
  if (p.from) return { ...p.from, id: p.id, status: 'pending', failureReason: null, completedAt: null, podPhotoUrl: null, podPhotoUrls: null, paidHow: null, paidAmount: null, paidToCourier: null, courierNote: null, orderHint: p.hint ?? p.from.orderHint ?? 'auto', ...(p.copied ? { recipientName: p.name ?? null, recipientPhone: p.phone ?? null, notes: p.notes ?? null } : {}) };
  return storedStop({
    id: p.id,
    address: p.address,
    writtenAddress: p.writtenAddress ?? null,
    placeId: p.placeId ?? null,
    houseNumber: p.houseNumber ?? null,
    lat: p.lat,
    lng: p.lng,
    recipientName: p.name ?? null,
    recipientPhone: p.phone ?? null,
    notes: p.notes ?? null,
    precision: p.precision ?? 'exact',
    orderHint: p.hint ?? 'auto',
  });
}

/** A route's name from the time of day it's sent: "Morning run", "Afternoon run", "Evening run". */
export function runName(m: Model, uid: string): string {
  const h = wall(m.now, m.zone).hour;
  const base = h < 12 ? 'Morning run' : h < 17 ? 'Afternoon run' : 'Evening run';
  const names = new Set(m.routes.filter((r) => r.riderId === uid).map((r) => r.name));
  let name = base;
  for (let k = 2; names.has(name); k++) name = `${base} ${k}`;
  return name;
}

/**
 * Where a route starts when the place is set but no route has started there yet (its position isn't kept): looked up
 * through Routr's server as it's sent, by its place ID or its address. Null when it can't be found.
 */
async function placeStart(m: Model): Promise<{ label: string; lat: number; lng: number } | null> {
  const p = m.place;
  if (!p) return null;
  try {
    const r = p.placeId ? (await lookup('details', { place_id: p.placeId })).result : ((await lookup('geocode', { address: p.address })).results as unknown[] | undefined)?.[0];
    const gp = isRecord(r) ? googlePlace(r, !p.placeId) : null;
    return gp && !gp.areaOnly ? { label: p.label, lat: gp.lat, lng: gp.lng } : null;
  } catch {
    return null;
  }
}

/** Sends a planned route: shown waiting at once, written after 5 s with Google's order and times. */
export function sendPlan(plan: Plan) {
  const m = model();
  const uid = m.business!.id;
  const p = plan.to ? m.person(plan.to) : null;
  if (!p || !plan.stops.length) return;
  // The start as the owner set it, else where routes start; the order as chosen or sorted, else Routr sorts it now.
  const start = planStart(m, plan);
  const chosen = !!plan.order;
  const orderedPlan = planOrder(m, plan);
  const stops = orderedPlan.map(planStopToStored);
  const sorted = plan.timed && plan.timed.order.join() === orderedPlan.map((x) => x.id).join() ? plan.timed : null;
  // Split into runs: each run a loop from where the courier loads, written as the phone's split route (`runs`, `load_at`).
  const runPlan = planRuns(m, plan);
  if (runPlan) return sendRuns(m, plan, runPlan, p.uid);
  const name = plan.name.trim() || runName(m, p.uid);
  const until = plan.stops.map((s) => s.googleUntil).filter((t): t is number => t != null);
  const googleUntil = until.length ? Math.min(...until) : null;
  const ref = write.newRouteRef();
  const shownTiming = sorted ?? straight(start, stops.map(pointOf), undefined, !!plan.back);
  const byId = new Map(stops.map((s) => [s.id, s]));
  const shown = write.sendFields({ businessId: uid, riderId: p.uid, stops: shownTiming.order.map((id) => byId.get(id)!), optimization: optimizationJson(shownTiming, !!start), name, start, googleUntil });
  pendRoute(ref.id, { ...shown, sentAt: Date.now() });
  // A stop that came off a route waiting to start leaves it in the same send (no second wait: this one had its Undo).
  const leaving = plan.stops.map((s) => s.fromKey).filter((k): k is string => !!k && !cantChange(m, m.raw(k.slice(0, k.indexOf('/')))));
  const off = takeOffPlan(m, leaving);
  const offShown = new Map<string, Doc | null>();
  for (const c of off.changes) offShown.set(c.routeId, retimed(c.raw, c.stops, straight(startOf(c.raw), c.stops.filter(placed).map(pointOf))));
  for (const id of off.gone) offShown.set(id, null);
  for (const [id, doc] of offShown) pendRoute(id, doc);
  const unpendOff = () => offShown.forEach((doc, id) => unpendRouteIf(id, doc));
  // Waiting stops leave the list as it's shown sent, and come back with Undo or if the send fails.
  const waitingIds = plan.stops.map((s) => s.waitingId).filter((id): id is string => !!id);
  const fromWaiting = fromWaitingOf(m, waitingIds);
  if (waitingIds.length) pendWaiting(waitingIds, () => null);
  ui.set({ plan: null, view: 'day', route: null, stop: null });
  hold(`Sending to ${p.first} · ${plural(stops.length, 'stop')}`, {
    send: async () => {
      const from = start ?? (plan.start === undefined ? await placeStart(m) : null);
      const t =
        sorted ??
        (chosen
          ? await timeRoute(from, stops.map(pointOf), { optimise: false, back: !!plan.back })
          : await sortStops(from, orderedPlan.map((x) => ({ id: x.id, lat: x.lat, lng: x.lng, hint: x.hint ?? 'auto', place: x.place, pickupId: (x.from?.pickupId as string | undefined) ?? null })), !!plan.back));
      await write.sendRoute(ref, { businessId: uid, riderId: p.uid, stops: t.order.map((id) => byId.get(id)!), optimization: optimizationJson(t, !!from), name, start: from, googleUntil }, waitingIds, waitingIds.length ? fromWaiting : undefined);
      unpendRoute(ref.id);
      unpendWaiting(waitingIds);
      track('route_sent', { stops: stopsBucket(stops.length), timed: t.method, from_start: !!from, off_other_routes: off.n > 0, from_waiting: waitingIds.length > 0 });
      let note = t.method === 'haversine' && t.why ? `Sent to ${p.first}. Timed by straight lines: ${t.why.toLowerCase()}` : `Sent to ${p.first}. It waits on their map until they start it.`;
      if (off.n) {
        try {
          await writeTakeOff(off.changes, off.gone);
        } catch (e) {
          // The route went; only the old copies stayed. Said as it is, not as a failed send.
          console.warn(e);
          note = `Sent to ${p.first}, but ${plural(off.n, 'stop')} stayed on the route they came from: take them off there`;
        } finally {
          unpendOff();
        }
      }
      say(note, 6000);
    },
    undo: () => {
      unpendRoute(ref.id);
      unpendOff();
      unpendWaiting(waitingIds);
      ui.set({ plan, view: 'plan' });
    },
    failed: waitingIds.length
      ? 'Couldn’t send it: some of its stops have just been sent or removed (another tab?), or the connection dropped. They’re as they were; check Waiting.'
      : undefined,
  });
}

/** Sends a plan split into runs: the stops run by run, each run timed from the load point as it's sent (Google's). */
function sendRuns(m: Model, plan: Plan, rp: { runs: RunStop[][]; depot: Spot }, riderId: string) {
  const uid = m.business!.id;
  const p = m.person(riderId)!;
  const start = planStart(m, plan)!;
  const byId = new Map(plan.stops.map((x) => [x.id, planStopToStored(x)]));
  const name = plan.name.trim() || runName(m, riderId);
  const until = plan.stops.map((s) => s.googleUntil).filter((t): t is number => t != null);
  const googleUntil = until.length ? Math.min(...until) : null;
  const ref = write.newRouteRef();
  const json = (t: Timed & { runs: RunSpanJson[] }) => ({
    ...optimizationJson({ ...t, backS: undefined }, true),
    runs: t.runs,
    load_at: [rp.depot.lat, rp.depot.lng],
    ...(plan.back && t.backS != null ? { return_to: [rp.depot.lat, rp.depot.lng], return_s: Math.round(t.backS) } : {}),
  });
  const waitingIds = plan.stops.map((s) => s.waitingId).filter((id): id is string => !!id);
  const fromWaiting = fromWaitingOf(m, waitingIds);
  void timeRuns(rp.runs, rp.depot, !!plan.back, false).then((shownT) => {
    pendRoute(ref.id, { ...write.sendFields({ businessId: uid, riderId, stops: shownT.order.map((id) => byId.get(id)!), optimization: json(shownT), name, start, googleUntil }), sentAt: Date.now() });
  });
  if (waitingIds.length) pendWaiting(waitingIds, () => null);
  ui.set({ plan: null, view: 'day', route: null, stop: null });
  const n = plan.stops.length;
  hold(`Sending to ${p.first} · ${plural(n, 'stop')} in ${rp.runs.length} runs`, {
    send: async () => {
      const t = await timeRuns(rp.runs, rp.depot, !!plan.back, true);
      await write.sendRoute(ref, { businessId: uid, riderId, stops: t.order.map((id) => byId.get(id)!), optimization: json(t), name, start, googleUntil }, waitingIds, waitingIds.length ? fromWaiting : undefined);
      unpendRoute(ref.id);
      unpendWaiting(waitingIds);
      track('route_sent', { stops: stopsBucket(n), timed: t.method, from_start: true, runs: rp.runs.length, from_waiting: waitingIds.length > 0 });
      say(t.method === 'haversine' && t.why ? `Sent to ${p.first} in ${rp.runs.length} runs. Timed by straight lines: ${t.why.toLowerCase()}` : `Sent to ${p.first} in ${rp.runs.length} runs, loading at ${start.label} between them.`, 6000);
    },
    undo: () => {
      unpendRoute(ref.id);
      unpendWaiting(waitingIds);
      ui.set({ plan, view: 'plan' });
    },
    failed: waitingIds.length ? 'Couldn’t send it: some of its stops have just been sent or removed (another tab?), or the connection dropped. They’re as they were; check Waiting.' : undefined,
  });
}

// ── Stops waiting for a route (docs/waiting-stops.md) ──────────────────

/** A stop for the waiting list: as stored (`storedStop`), where it came from, its day, and Google's position if found. */
export interface NewWaiting {
  stop: Doc & { id: string; address: string };
  source: Source;
  sheet?: { file: string; row: number } | null;
  day?: string | null;
  /** Google's position and when it was looked up; a customer's own pin goes on the stop instead. */
  google?: { lat: number; lng: number; at: number } | null;
}

/** A waiting stop's record as last known here: what's pending, else what was read. */
const rawWaiting = (id: string): Doc | null => pending.get().waiting.get(id) ?? data.get().waiting.get(id) ?? null;

/** Adds stops to the waiting list: shown at once, written straight away (no one is told). */
export function addWaiting(list: NewWaiting[], done?: string) {
  const m = model();
  const uid = m.business!.id;
  const at = Date.now();
  const already = list.filter((x) => data.get().waiting.has(x.stop.id) || pending.get().waiting.get(x.stop.id));
  const refused = list.filter((x) => !already.includes(x) && problemOf(x.stop, x.day));
  const go = list.filter((x) => !already.includes(x) && !refused.includes(x));
  const recs = go.map((x) => ({ id: x.stop.id, fields: waitingFields({ businessId: uid, ...x, now: at }) }));
  const notes = [
    already.length ? `${plural(already.length, 'stop')} ${already.length === 1 ? 'was' : 'were'} already waiting, so not added twice` : '',
    ...refused.map((x) => `${x.sheet ? `Row ${x.sheet.row}` : String(x.stop.address).slice(0, 40)} not added: ${problemOf(x.stop, x.day)}`),
  ].filter(Boolean);
  if (notes.length) setTimeout(() => say(notes.join('. '), 8000), recs.length ? 1500 : 0);
  if (!recs.length) return;
  const ids = recs.map((r) => r.id);
  const byId = new Map(recs.map((r) => [r.id, r.fields]));
  pendWaiting(ids, (id) => byId.get(id)!);
  now({
    write: async () => {
      await write.addWaiting(uid, recs);
      unpendWaiting(ids);
      track('waiting_added', { source: list[0].source, stops: stopsBucket(recs.length), with_day: list.some((x) => !!x.day) });
    },
    undo: () => unpendWaiting(ids),
    // The rows were checked first, so a refusal is most likely this computer's clock (more than 3 hours off).
    failed: 'Couldn’t add them. Check the connection, and that this computer’s date and time are right; adding them again only adds what’s missing.',
    done: done ?? `${plural(recs.length, 'stop')} waiting for a route`,
  });
}

/** The day they're for, or none. */
export function setWaitingDay(ids: string[], day: string | null) {
  const m = model();
  const uid = m.business!.id;
  const shown = new Map(ids.map((id) => [id, rawWaiting(id)]).filter((x): x is [string, Doc] => !!x[1]).map(([id, d]) => {
    const { day: _old, ...rest } = d;
    return [id, day ? { ...rest, day } : rest];
  }));
  if (!shown.size) return;
  const keys = [...shown.keys()];
  pendWaiting(keys, (id) => shown.get(id)!);
  now({
    write: async () => {
      await write.setWaitingDay(uid, keys, day);
      unpendWaiting(keys);
      track('waiting_day_set', { stops: stopsBucket(keys.length), cleared: !day });
    },
    undo: () => unpendWaiting(keys),
    done: day ? `${plural(keys.length, 'stop')} for ${dayName(m, day)}` : `${plural(keys.length, 'stop')} with no day`,
  });
}

/** "today", "tomorrow", "Tuesday 3 November". */
export function dayName(m: Model, day: string): string {
  if (day === m.today) return 'today';
  const [y, mo, d] = m.today.split('-').map(Number);
  const tomorrow = new Date(Date.UTC(y, mo - 1, d + 1)).toISOString().slice(0, 10);
  if (day === tomorrow) return 'tomorrow';
  return dayWords(day);
}

/** Off the list, customers and all: 5 seconds with Undo first, as it can't be brought back once written. */
export function removeWaiting(ids: string[]) {
  const m = model();
  const uid = m.business!.id;
  const gone = ids.filter((id) => rawWaiting(id));
  if (!gone.length) return;
  pendWaiting(gone, () => null);
  const s = ui.get();
  if (s.rec?.type === 'waiting' && gone.includes(s.rec.id)) ui.set({ rec: null });
  ui.set({ sel: new Set() });
  hold(`${plural(gone.length, 'stop')} removed from Waiting`, {
    send: async () => {
      await write.removeWaiting(uid, gone);
      unpendWaiting(gone);
      track('waiting_removed', { stops: stopsBucket(gone.length) });
    },
    undo: () => unpendWaiting(gone),
    failed: 'Couldn’t remove them. Check your connection and try again.',
  });
}

/** Where each waiting stop came from, kept on the route (or sent stop) it goes onto, so taking that back returns it. */
export function fromWaitingOf(m: Model, ids: string[]): Doc {
  return Object.fromEntries(
    ids.flatMap((id) => {
      const w = m.waiting.find((x) => x.id === id);
      return w ? [[id, { source: w.source, ...(w.sheet ? { sheet: w.sheet } : {}), ...(w.day ? { day: w.day } : {}), ...(w.byTime ? { byTime: w.byTime } : {}), expiresAt: w.expiresAt, pin: !!w.pin }]] : [];
    }),
  );
}

/**
 * Waiting stops made ready to go onto a route, as [PlanStop]s with their positions. A customer's pin and a position Google
 * gave in the last day go as they are; anything older is looked up again first (its place ID, else its address), so the
 * route's 29 days with Google's positions start now, and the fresh position is kept on the waiting stop too. One that
 * can't be found stays waiting: [missing], with [why] in words.
 */
async function placeWaiting(m: Model, ids: string[]): Promise<{ ps: PlanStop[]; missing: Waiting[]; why: string; found: number } | null> {
  const uid = m.business!.id;
  const t = Date.now();
  const ws = ids.map((id) => m.waiting.find((w) => w.id === id)).filter((w): w is Waiting => !!w);
  if (!ws.length) return null;
  const again = ws.filter((w) => !readyToPlan(w, t));
  const found = new Map<string, { lat: number; lng: number; placeId?: string }>();
  let error: string | null = null;
  if (again.length) {
    toast(again.length === 1 ? `Finding ${again[0].street} on the map again…` : `Finding ${again.length} stops on the map again…`, { ms: 60_000 });
    const res = await findAgain(m, again.map((w) => ({ placeId: w.placeId, address: String(w.stop.writtenAddress ?? w.address) })));
    error = res.error;
    again.forEach((w, i) => res.places[i] && found.set(w.id, { lat: res.places[i]!.lat, lng: res.places[i]!.lng, placeId: res.places[i]!.placeId }));
    // Kept on the waiting stops too (29 days from now), so planning them again doesn't look them up again.
    if (found.size) now({ write: () => write.refreshWaitingGoogle(uid, again.flatMap((w) => { const p = found.get(w.id); return p ? [{ id: w.id, lat: p.lat, lng: p.lng, until: t + GOOGLE_MS - CLOCK_MARGIN_MS, named: !w.placeId && p.placeId ? { placeId: p.placeId, written: String(w.stop.writtenAddress ?? w.address) } : null }] : []; })) });
  }
  const missing = again.filter((w) => !found.has(w.id));
  const ps: PlanStop[] = ws
    .filter((w) => !missing.includes(w))
    .map((w) => {
      const spot = found.get(w.id) ?? spotOf(w)!;
      return {
        id: w.id,
        waitingId: w.id,
        address: w.address,
        area: w.area,
        lat: spot.lat,
        lng: spot.lng,
        placeId: w.placeId ?? undefined,
        name: w.name ?? undefined,
        phone: w.phone ?? undefined,
        pin: !!w.pin,
        googleUntil: w.pin ? null : found.has(w.id) ? t + GOOGLE_MS - CLOCK_MARGIN_MS : w.googleUntil,
        // A deliver-by time with no day takes the day it goes out: its own day if it has one, else today. A position just
        // found keeps its place ID, so no one takes it for the customer's own.
        from: { ...w.stop, ...(found.get(w.id) && !w.placeId ? { placeId: found.get(w.id)!.placeId ?? null, writtenAddress: w.stop.writtenAddress ?? w.address } : {}), lat: spot.lat, lng: spot.lng, deliverBy: w.stop.deliverBy ?? deadlineOn(w.day ?? m.today, w.byTime) },
      };
    });
  const why = error ?? (missing.length === 1 ? `Couldn’t find ${missing[0].street} on the map again: check its address` : `Couldn’t find ${plural(missing.length, 'stop')} on the map again: check their addresses`);
  return { ps, missing, why, found: found.size };
}

/** Each of [items] found on the map again: by its place ID, else its address. Null for one that isn't found; [error] says why, if a lookup failed. */
async function findAgain(m: Model, items: { placeId: string | null; address: string }[]): Promise<{ places: (GooglePlace | null)[]; error: string | null }> {
  const errors: string[] = [];
  const places = await inTurn(items, async (x): Promise<GooglePlace | null> => {
    try {
      const byId = x.placeId ? await findPlaceId(x.placeId) : null;
      if (byId) return byId;
      const f = await findAddress(x.address, { country: m.business?.country, near: m.shop });
      if (f.error) errors.push(f.error);
      return f.place && f.rough ? { ...f.place, precision: 'low' } : f.place;
    } catch (e) {
      errors.push(e instanceof LookupError ? e.message : 'Routr’s server didn’t answer. Check the connection and try again.');
      return null;
    }
  });
  return { places, error: errors.at(-1) ?? null };
}

/**
 * "Copy to a new route": Plan a route opens with the same stops, under new ids, the route itself untouched. A customer's
 * pin and a position Google gave in the last day go as they are; anything older, or gone, is looked up again first (its
 * place ID, else its address), so the new route's 29 days start now. One that can't be found is left out, and said.
 */
export const copyRoute = (routeId: string) => once(() => copyStops(routeId));

async function copyStops(routeId: string) {
  const m = model();
  const r = m.route(routeId);
  // A past route's record isn't among today's.
  const raw = m.raw(routeId) ?? m.data.history?.get(routeId);
  if (!r || !raw) return toast('That route has gone');
  const records = (Array.isArray(raw.stops) ? (raw.stops as Doc[]) : []).filter(isRecord);
  const sent = raw.status === 'sent' || raw.status === 'declined';
  // A started route's customers are in its working copy, while it has one.
  const stops = sent ? records : records.map((x) => ({ ...storedFromRecord(m, raw, x, x.lat as number, x.lng as number), precision: x.precision ?? 'exact', pickupId: x.pickupId ?? null, orderHint: x.orderHint ?? 'auto' }));
  const t = Date.now();
  const until = ms(raw.googleUntil) ?? (r.sentAt ?? r.startedAt ?? 0) + GOOGLE_MS;
  const all = copiedStops(stops, raw, { until, now: t, today: m.today, zone: m.zone, newId: () => crypto.randomUUID() });
  if (!all.length) return toast('That route has no stops to copy');
  const again = all.filter((c) => c.again);
  let res: Awaited<ReturnType<typeof findAgain>> = { places: [], error: null };
  if (again.length) {
    toast(again.length === 1 ? 'Finding its stop on the map again…' : `Finding ${again.length} stops on the map again…`, { ms: 60_000 });
    res = await findAgain(m, again.map((c) => ({ placeId: (c.stored.placeId as string | null) ?? null, address: String(c.stored.writtenAddress ?? c.stored.address) })));
  }
  const placeOf = new Map(again.map((c, i) => [c, res.places[i] ?? null]));
  const ps = all.flatMap((c): PlanStop[] => {
    const place = placeOf.get(c);
    if (c.again && !place) return [];
    const from = place ? placedAgain(c.stored, place) : c.stored;
    const address = String(from.address);
    return [
      {
        id: String(from.id),
        address,
        writtenAddress: (from.writtenAddress as string | null) ?? undefined,
        area: splitAddress(address).area,
        lat: from.lat as number,
        lng: from.lng as number,
        placeId: (from.placeId as string | null) ?? undefined,
        houseNumber: (from.houseNumber as string | null) ?? undefined,
        precision: from.precision === 'low' ? 'low' : 'exact',
        name: (from.recipientName as string | null) ?? undefined,
        phone: (from.recipientPhone as string | null) ?? undefined,
        notes: (from.notes as string | null) ?? undefined,
        pin: c.pin,
        googleUntil: c.pin ? null : place ? t + GOOGLE_MS - CLOCK_MARGIN_MS : until,
        hint: from.orderHint === 'first' || from.orderHint === 'last' ? from.orderHint : undefined,
        from,
        copied: true,
      },
    ];
  });
  const missing = all.length - ps.length;
  const why = res.error ?? `Couldn’t find ${plural(missing, 'stop')} on the map again`;
  if (!ps.length) return toast(why, { ms: 6000 });
  planWith(ps);
  track('route_copied', { state: r.state, stops: stopsBucket(ps.length), looked_up_again: stopsBucket(again.length), not_found: missing });
  const nobody = r.state === 'done' || r.state === 'ended';
  say(
    [
      missing ? `${why.replace(/[.\s]+$/, '')}: left out` : `${plural(ps.length, 'stop')} in the new route`,
      // Said because the record is built that way: /privacy says customers' details go when a route ends.
      nobody ? 'a finished route’s record has no names, phones or notes' : 'pick who takes it',
    ].join(' · '),
    nobody || missing ? 6000 : undefined,
  );
}

/** "They stay in Waiting", after what went wrong finding some of them again. */
const stayWaiting = (why: string, n: number) => `${why.replace(/[.\s]+$/, '')}. ${n === 1 ? 'It stays' : 'They stay'} in Waiting.`;

/** A second click while the first is still finding stops again would ask Google twice. */
let finding = false;
async function once(f: () => Promise<void>) {
  if (finding) return;
  finding = true;
  try {
    await f();
  } finally {
    finding = false;
  }
}

/**
 * Plan a route from waiting stops: each goes into the new route with its position (found again first if Google placed it
 * more than a day ago).
 */
export const planFromWaiting = (ids: string[]) => once(() => planWaiting(ids));

async function planWaiting(ids: string[]) {
  const m = model();
  const got = await placeWaiting(m, ids);
  if (!got) return;
  const { ps, missing, why, found } = got;
  if (!ps.length) return toast(why, { ms: 6000 });
  planWith(ps);
  track('waiting_planned', { stops: stopsBucket(ps.length), looked_up_again: stopsBucket(found), not_found: missing.length });
  if (missing.length) say(stayWaiting(why, missing.length), 6000);
  else toast(`${plural(ps.length, 'stop')} in the new route · pick who takes it`);
}

/**
 * "Add to a route" for waiting stops: onto a route waiting to start ([to.routeId]), each where it fits best, timed again;
 * or, for a courier already on a route ([to.riderId]), sent to them one at a time to fit in, since a started route is
 * theirs. Found again first if Google placed them more than a day ago; 5 s with Undo; they leave Waiting in the same
 * write, and taking the route (or the stop) back returns them.
 */
export const addWaitingTo = (ids: string[], to: { routeId: string } | { riderId: string }) =>
  once(() => ('routeId' in to ? addToWaitingRoute(ids, to.routeId) : sendWaitingTo(ids, to.riderId)));

async function addToWaitingRoute(ids: string[], routeId: string) {
  const before = model();
  const why0 = cantChange(before, before.raw(routeId));
  if (why0) return toast(why0);
  const got = await placeWaiting(before, ids);
  if (!got) return;
  const { ps, missing, why: notFound } = got;
  if (!ps.length) return toast(notFound, { ms: 6000 });
  // The route as it is now: it may have changed while stops were found again.
  const m = model();
  const raw = m.raw(routeId);
  const r = m.route(routeId);
  const why = cantChange(m, raw);
  if (why || !raw || !r) return toast(why ?? 'That route has gone');
  const was = raw.stops as Doc[];
  const have = new Set(was.map((x) => String(x.id)));
  const adding = ps.filter((p) => !have.has(p.id));
  if (!adding.length) return toast(ps.length === 1 ? 'That stop is on this route already' : 'Those stops are on this route already');
  let stops = [...was];
  for (const p of adding) {
    const stored = planStopToStored(p);
    stops.splice(fitIndex(startOf(raw), stops, { lat: p.lat, lng: p.lng }), 0, stored);
  }
  stops = pickupsFirst(stops);
  const waitingIds = adding.map((p) => p.waitingId!);
  const fromWaiting = { ...(isRecord(raw.fromWaiting) ? raw.fromWaiting : {}), ...fromWaitingOf(m, waitingIds) };
  // Google's positions in it go with the oldest of them: the route's own date, or an added stop's.
  const untils = [ms(raw.googleUntil), ...adding.map((p) => p.googleUntil ?? null)].filter((t): t is number => t != null);
  const googleUntil = untils.length ? Math.min(...untils) : null;
  const shown = { ...retimed(raw, stops, straight(startOf(raw), stops.filter(placed).map(pointOf))), fromWaiting };
  pendRoute(routeId, shown);
  pendWaiting(waitingIds, () => null);
  const s = ui.get();
  if (s.rec?.type === 'waiting' && waitingIds.includes(s.rec.id)) ui.set({ rec: null });
  ui.set({ sel: new Set() });
  const who = first(m, r.riderId);
  const title = routeTitle(r, m.zone);
  const undo = () => {
    unpendRouteIf(routeId, shown);
    unpendWaiting(waitingIds);
  };
  hold(`${plural(adding.length, 'stop')} added to ${who}’s ${title}${missing.length ? ` · ${missing.length} not found again` : ''}`, {
    send: async () => {
      const t = await timedAgain(raw, stops);
      await write.addToRoute({ businessId: m.business!.id, routeId, was: was.map((x) => String(x.id)), stops, optimization: t.optimization as Doc, fromWaiting, googleUntil, waitingIds });
      undo();
      track('waiting_added_to_route', { stops: stopsBucket(adding.length), looked_up_again: stopsBucket(got.found), not_found: missing.length, timed: String((t.optimization as Doc).method) });
    },
    undo,
    failed: (e) =>
      e instanceof write.WaitingGone
        ? 'Nothing was added: some of those stops have just been sent or removed (another tab?). Check Waiting.'
        : e instanceof write.RouteChanged
          ? e.why === 'started'
            ? `${who} has started the ${title}, so nothing was added: they’re still in Waiting. Send them to ${who} instead.`
            : e.why === 'gone'
              ? `The ${title} has been taken back, so nothing was added: they’re still in Waiting.`
              : `The ${title} changed in another tab, so nothing was added: they’re still in Waiting. Try again.`
          : 'Couldn’t add them: check the connection. They’re still in Waiting.',
    sent: `Added: ${who} has ${plural(stops.length, 'stop')} on the ${title} now`,
  });
  if (missing.length) say(stayWaiting(notFound, missing.length), 6000);
}

async function sendWaitingTo(ids: string[], riderId: string) {
  const m0 = model();
  const p = m0.person(riderId);
  if (!p) return;
  const ws = ids.map((id) => m0.waiting.find((w) => w.id === id)).filter((w): w is Waiting => !!w);
  // A delivery waiting is at the shop: the courier collects it there first, then delivers it (the phone's handover).
  if (ws.some((w) => w.type !== 'pickup') && !m0.shop)
    return toast(
      m0.place
        ? `${m0.place.label} isn’t on the map yet, so ${p.first} wouldn’t know where to collect ${ws.length === 1 ? 'it' : 'them'}: it shows once a route has started there. Plan a new route instead.`
        : `Set where routes start first, so ${p.first} knows where to collect ${ws.length === 1 ? 'it' : 'them'}: Finish setting up, or your name at the top right.`,
      { ms: 6000 },
    );
  const got = await placeWaiting(m0, ids);
  if (!got) return;
  const { ps, missing, why: notFound } = got;
  if (!ps.length) return toast(notFound, { ms: 6000 });
  const m = model();
  const shop = m.shop;
  const uid = m.business!.id;
  const items = ps.flatMap((x) => {
    const w = m.waiting.find((y) => y.id === x.waitingId);
    if (!w) return [];
    const stop = planStopToStored(x);
    const fields: write.SendStop =
      w.type === 'pickup' || !shop
        ? { businessId: uid, riderId, kind: 'pickup', stop }
        : {
            businessId: uid,
            riderId,
            kind: 'handover',
            stop,
            holderId: null,
            holderName: m.business?.name ?? null,
            holder: 'shop',
            takerName: p.name,
            takerPhone: p.courier.phone ?? null,
            takerPhoto: p.courier.photoUrl ?? null,
            meet: { label: shop.label, lat: shop.lat, lng: shop.lng },
          };
    return [{ ref: write.newSentStopRef(), fields, waitingId: w.id, fromWaiting: fromWaitingOf(m, [w.id]) }];
  });
  if (!items.length) return;
  const waitingIds = items.map((x) => x.waitingId);
  for (const x of items) pendSentStop(x.ref.id, { ...write.sentStopFields(x.fields), fromWaiting: x.fromWaiting, sentAt: Date.now() });
  pendWaiting(waitingIds, () => null);
  const s = ui.get();
  if (s.rec?.type === 'waiting' && waitingIds.includes(s.rec.id)) ui.set({ rec: null });
  ui.set({ sel: new Set() });
  const undo = () => {
    for (const x of items) unpendSentStop(x.ref.id);
    unpendWaiting(waitingIds);
  };
  const collect = items.some((x) => x.fields.kind === 'handover');
  const them = items.length === 1 ? 'it' : 'them';
  hold(`Sending ${plural(items.length, 'stop')} to ${p.first}${missing.length ? ` · ${missing.length} not found again` : ''}`, {
    send: async () => {
      await write.sendFromWaiting(uid, items);
      undo();
      track('sent_stop_sent', { kind: collect ? 'handover' : 'pickup', from_waiting: true, stops: stopsBucket(items.length) });
    },
    undo,
    failed: (e) =>
      e instanceof write.WaitingGone
        ? 'Nothing was sent: some of those stops have just been sent or removed (another tab?). Check Waiting.'
        : 'Couldn’t send them: check the connection. They’re still in Waiting.',
    sent: collect ? `Sent to ${p.first}: they collect ${them} at ${shop!.label}, then deliver ${them}. It shows on their map to fit in.` : `Sent to ${p.first}: ${items.length === 1 ? 'it shows' : 'they show'} on their map to fit in.`,
  });
  if (missing.length) say(stayWaiting(notFound, missing.length), 6000);
}

// ── Stops sent one at a time ─────────────────────────────────────────────

export type TryWay = 'tryAgain' | 'bringBack' | 'handover';

/** The stops sent on their own for [s] (Try again, Bring it back, a handover), oldest first. */
export const sentFor = (m: Model, s: Stop) => m.sentStops.filter((x) => x.fromRouteId === s.routeId && x.fromStopId === s.id).sort((a, b) => (a.sentAt ?? 0) - (b.sentAt ?? 0));

/** One already out for [s]: sent, on a courier's route, or collected (the phone offers Try again only without one). */
export const retryOut = (m: Model, s: Stop) => sentFor(m, s).find((x) => x.status === 'sent' || x.status === 'added' || x.status === 'collected') ?? null;

/**
 * Try again (live_screen.dart `_tryAgain`): the courier who tried takes it again, brings it back to the shop, or
 * someone else collects it from them where they meet. The customer's link goes with the parcel; not to the shop.
 */
export function tryAgain(key: string, way: TryWay, taker?: Person | null, meet?: write.SendStop['meet']) {
  const m = model();
  const s = m.stop(key);
  const r = s ? m.route(s.routeId) : null;
  const holder = s ? m.person(s.riderId) : null;
  if (!s || !r || !holder) return;
  if (r.state !== 'running') return toast(`${holder.first}’s route has ended: plan it on a new route instead`);
  const stop = stopForSending(m, s.routeId, s.id);
  if (!stop) return toast('This stop has no position to send');
  if (way === 'bringBack' && !meet) return toast(m.place ? `${m.place.label} isn’t on the map yet: it shows once a route has started there` : 'Set where routes start first: Finish setting up, or your name at the top right');
  const out = retryOut(m, s);
  if (out) return toast(`Already sent to ${first(m, out.riderId)}: it’s on its way to them`);
  // A declined one goes in the same write (SentStopsService.send `replaces`), so its line leaves Needs a look.
  const replaces = sentFor(m, s).filter((x) => x.status === 'declined').at(-1)?.id ?? null;
  const riderId = way === 'handover' ? taker?.uid : holder.uid;
  if (!riderId) return;
  const why = [`Not delivered${s.at != null ? ` ${time(s.at, m.zone)}` : ''}`, s.reason ? reasonOf(s) : null].filter(Boolean).join(' · ');
  const fields: write.SendStop = {
    businessId: m.business!.id,
    riderId,
    kind: way,
    stop,
    why,
    fromRouteId: s.routeId,
    fromStopId: s.id,
    trackingCode: way === 'bringBack' ? null : (s.trackingCode ?? null),
    holderId: way === 'handover' ? holder.uid : null,
    holderName: way === 'handover' ? holder.name : null,
    holderPhone: way === 'handover' ? (holder.courier.phone ?? null) : null,
    takerName: way === 'handover' && taker ? taker.name : null,
    takerPhone: way === 'handover' ? (taker?.courier.phone ?? null) : null,
    holderPhoto: way === 'handover' ? (holder.courier.photoUrl ?? null) : null,
    takerPhoto: way === 'handover' ? (taker?.courier.photoUrl ?? null) : null,
    meet: meet ?? null,
  };
  const ref = write.newSentStopRef();
  pendSentStop(ref.id, { ...write.sentStopFields(fields), sentAt: Date.now() });
  if (replaces) pendSentStop(replaces, null);
  const who = m.person(riderId)?.first ?? 'them';
  const msg =
    way === 'tryAgain' ? `Trying again: ${holder.first} still has it` : way === 'bringBack' ? `${holder.first} brings ${streetOf(s)} back to the shop` : `${who} collects ${streetOf(s)} from ${holder.first} at ${meet?.label ?? 'the meeting place'}`;
  hold(msg, {
    send: async () => {
      await write.sendStop(ref, fields, replaces);
      unpendSentStop(ref.id);
      if (replaces) unpendSentStop(replaces);
      track('sent_stop_sent', { kind: way, replaced_declined: !!replaces });
    },
    undo: () => {
      unpendSentStop(ref.id);
      if (replaces) unpendSentStop(replaces);
    },
    sent: `Sent to ${who}. It shows on their map to fit in.`,
  });
}

const reasonOf = (s: Stop) => ({ noOneThere: 'No one there', noAccess: 'Couldn’t get in', wrongAddress: 'Wrong address', refused: 'Refused', closed: 'Closed', anotherDay: 'Another day', couldntPay: 'Couldn’t pay', other: 'Other' })[s.reason ?? 'other'];

/** A stop sent on its own, taken back before it's on someone's route. */
export function takeBackSentStop(id: string) {
  const m = model();
  const x = m.sentStops.find((s) => s.id === id);
  if (!x) return;
  if (x.status !== 'sent' && x.status !== 'declined' && x.status !== 'done') return toast('It’s on their route now: it comes off there');
  // One sent from Waiting goes back there (not once it's done), found again when it's planned.
  const restore = x.status !== 'done' && isRecord(x.data.fromWaiting) && isRecord(x.data.stop) ? restoreFrom(m, x.data.fromWaiting, [x.data.stop], null) : [];
  pendSentStop(id, null);
  if (restore.length) pendWaiting(restore.map((r) => r.id), (rid) => restore.find((r) => r.id === rid)!.fields);
  hold(`Taken back · off ${first(m, x.riderId)}’s list${restore.length ? ' · back in Waiting' : ''}`, {
    send: async () => {
      await write.takeBackStop(id, { businessId: m.business!.id, items: restore });
      unpendSentStop(id);
      unpendWaiting(restore.map((r) => r.id));
      track('sent_stop_taken_back', { kind: x.kind, to_waiting: restore.length > 0 });
    },
    undo: () => {
      unpendSentStop(id);
      unpendWaiting(restore.map((r) => r.id));
    },
  });
}

/** A new pickup, from Who's closest (closest_sheet.dart): it opens on the courier's map to fit in. */
export function sendPickup(riderId: string, at: { lat: number; lng: number; address: string; placeId?: string }) {
  const m = model();
  const p = m.person(riderId);
  if (!p) return;
  const fields: write.SendStop = {
    businessId: m.business!.id,
    riderId,
    kind: 'pickup',
    stop: storedStop({ id: `pickup-${Date.now()}`, address: at.address, placeId: at.placeId ?? null, lat: at.lat, lng: at.lng, type: 'pickup' }),
  };
  const ref = write.newSentStopRef();
  pendSentStop(ref.id, { ...write.sentStopFields(fields), sentAt: Date.now() });
  hold(`Sending the pickup to ${p.first}`, {
    send: async () => {
      await write.sendStop(ref, fields);
      unpendSentStop(ref.id);
      track('sent_stop_sent', { kind: 'pickup', courier_busy: !!p.day?.running });
    },
    undo: () => unpendSentStop(ref.id),
    sent: p.day?.running ? `Sent to ${p.first}: they fit it into their route.` : `Sent to ${p.first}: it opens on their map, ready to plan.`,
  });
}

// ── Needs a look ─────────────────────────────────────────────────────────

/** Clear, with a note or without (LiveAlertsService.clear): it can't be taken back once written, so it waits 5 s. */
export function clearLook(x: Look, note?: string) {
  const m = model();
  if (!x.alert || !write.CLEARABLE.has(x.alert.kind)) return;
  const alert = x.alert;
  pendCleared(alert.id);
  ui.set({ noting: null });
  const text = note?.trim();
  hold(text ? `Cleared: “${text.slice(0, 60)}”` : 'Cleared', {
    send: async () => {
      await write.clearAlert({ businessId: m.business!.id, alertId: alert.id, kind: alert.kind, day: m.today, note: text });
      unpendCleared(alert.id);
      track('live_alert_cleared', { kind: alert.kind, with_note: !!text });
    },
    undo: () => unpendCleared(alert.id),
    failed: 'Couldn’t clear it. Check your connection and try again.',
  });
}

/** "Remind Musa": the server pushes the courier to end a route still open (the `routeRemind` callable). */
export function remindCourier(routeId: string) {
  const m = model();
  const r = m.route(routeId) ?? null;
  const name = r ? first(m, r.riderId) : 'them';
  write.remind(routeId).then(
    (res) => (
      track('live_action', { action: 'remind', sent: !!res.sent }),
      toast(
        res.ended ? `${name} has ended that route already` : res.sent ? `Reminded ${name}` : `${name} was reminded ${res.remindedAt ? `at ${time(res.remindedAt, m.zone)}` : 'a moment ago'}; one reminder every 30 minutes`,
      )
    ),
    () => toast('Couldn’t send the reminder. Check your connection.'),
  );
}

export { whatsAppNumber } from './words';

/** "Send link" (TrackingShare.send): WhatsApp with the message when there's a number, else the message copied. */
export function sendLink(s: Stop) {
  const m = model();
  if (!s.trackingCode) return toast('No tracking link on this stop yet');
  const who = m.person(s.riderId)?.first;
  const link = (c: string) => `https://goroutr.com/t/${s.trackingCode}?c=${c}`;
  const message = (c: string) => `Hi! ${s.type === 'pickup' ? (who ? `${who} is on the way to collect your parcel.` : 'Your courier is on the way to collect your parcel.') : who ? `${who} is on the way with your delivery.` : 'Your delivery is on its way.'} Track it live: ${link(c)}`;
  const number = whatsAppNumber(s.phone, m.business?.country ?? null);
  track('live_action', { action: 'send_link', via: number ? 'whatsapp' : 'copied' });
  if (number) {
    window.open(`https://wa.me/${number}?text=${encodeURIComponent(message('link'))}`, '_blank', 'noopener');
    return toast(`WhatsApp opened for ${s.name ?? 'the customer'}: the message is ready to send`);
  }
  (navigator.clipboard?.writeText(message('share')) ?? Promise.reject()).then(
    () => toast('No number to message: the link’s message is copied to paste anywhere'),
    () => toast('Couldn’t copy the message. Try again.'),
  );
}

// ── Settling up ──────────────────────────────────────────────────────────

/** Where a balance starts again: its newest route money when a hand-in brings it to exactly zero (`zeroThroughAfter`). */
export function zeroThroughAfter(m: Model, riderId: string, settles: number): number | null {
  const b = balanceOf(riderId, m.data.routeMoney, m.data.handIns);
  if (b.amount - settles !== 0) return null;
  const through = b.routes.reduce<number | null>((t, r) => (r.at != null && (t == null || r.at > t) ? r.at : t), null);
  return through ?? 0;
}

/** Confirms a hand-in a courier said they made, with what came in (decision 2: any amount). */
export function confirmHandIn(h: HandIn, received: number) {
  const m = model();
  const p = m.person(h.riderId);
  const zero = zeroThroughAfter(m, h.riderId, received);
  pendHandIn(h.id, { riderId: h.riderId, amount: h.amount, received, out: h.out, status: 'confirmed', how: h.how, at: h.at, confirmedAt: Date.now(), ...(zero != null ? { zeroThrough: zero } : {}) });
  now({
    write: async () => {
      await write.confirmHandIn(h.id, received, zero);
      unpendHandIn(h.id);
      track('hand_in_answered', { how: received === h.amount ? 'confirmed' : 'different', settled: zero != null });
    },
    undo: () => unpendHandIn(h.id),
    done: `${money(received, m.currency)} from ${p?.first ?? 'the courier'}, confirmed`,
  });
}

export function handInNotReceived(h: HandIn) {
  const m = model();
  const p = m.person(h.riderId);
  pendHandIn(h.id, { riderId: h.riderId, amount: h.amount, out: h.out, status: 'notReceived', how: h.how, at: h.at, confirmedAt: Date.now() });
  now({
    write: async () => {
      await write.handInNotReceived(h.id);
      unpendHandIn(h.id);
      track('hand_in_answered', { how: 'not_received' });
    },
    undo: () => unpendHandIn(h.id),
    done: `Marked not received · it stays in ${p?.first ?? 'their'}’s balance, and they see that`,
  });
}

/** Money handed over the counter (or paid to an on-call courier), recorded by the owner. */
export function recordHandIn(riderId: string, amount: number, out: boolean) {
  const m = model();
  const p = m.person(riderId);
  if (!p || amount <= 0) return;
  const zero = zeroThroughAfter(m, riderId, out ? -amount : amount);
  now({
    write: async () => {
      await write.recordHandIn({ businessId: m.business!.id, riderId, amount, out, currency: m.currency, how: 'cash', zeroThrough: zero });
      track('hand_in_recorded', { out, settled: zero != null });
    },
    done: out ? `${money(amount, m.currency)} paid to ${p.first}, recorded` : `${money(amount, m.currency)} from ${p.first}, recorded`,
  });
}

/** Opens a route, from anywhere (⌘K, a menu). */
export const showRoute = (id: string) => openRoute(id);
export { keyOf };

// ── Where routes start ───────────────────────────────────────────────────

/** The business's places, the whole list (ProfileService.saveBusinessPlaces); the first is where routes start. */
export function saveStartPlaces(places: { label: string; address: string; placeId?: string }[]) {
  const m = model();
  now({
    write: async () => {
      await write.saveBusinessPlaces(m.business!.id, places);
      track('live_action', { action: places.length ? 'start_place_saved' : 'start_place_removed', looked_up: !!places[0]?.placeId });
    },
    done: places.length ? `Routes start at ${places[0].label}` : 'Where routes start is removed',
  });
}

// ── Zones (src/data/zones.ts) ────────────────────────────────────────────

/** The couriers' first names, in words: "Musa and Tunde". */
const namesOf = (m: Model, uids: string[]) => {
  const xs = uids.map((u) => m.person(u)?.first).filter((x): x is string => !!x);
  return xs.length <= 1 ? (xs[0] ?? '') : `${xs.slice(0, -1).join(', ')} and ${xs.at(-1)}`;
};

/** A zone just drawn, saved at once (no one is told: it only rules how stops are shared out). */
export function saveNewZone(d: { name: string; couriers: string[]; path: ZoneSpot[] }) {
  const m = model();
  const uid = m.business!.id;
  const why = zoneProblem(d);
  if (why) return toast(`Can’t save the zone: ${why}`);
  const ref = write.newZoneRef(uid);
  const fields = zoneFields({ businessId: uid, ...d }, true);
  ui.set({ drawing: null, zone: ref.id, reshaping: false });
  const who = namesOf(m, d.couriers);
  now({
    write: async () => {
      await write.saveZone(ref, fields, true);
      track('zone_drawn', { corners: d.path.length, couriers: d.couriers.length });
    },
    undo: () => ui.get().zone === ref.id && ui.set({ zone: null }),
    failed: 'Couldn’t save the zone. Check the connection and draw it again.',
    done: who ? `${d.name.trim()} saved: its stops go to ${who} when stops are shared out` : `${d.name.trim()} saved. Add its couriers: until then it doesn’t limit who gets its stops.`,
  });
}

/** A zone changed: its name, its couriers or its shape, written whole. */
function changeZone(id: string, change: { name?: string; couriers?: string[]; path?: ZoneSpot[] }, done?: string, kind = 'zone_changed') {
  const m = model();
  const z = m.zones.find((x) => x.id === id);
  if (!z) return;
  const next = { name: change.name ?? z.name, couriers: change.couriers ?? z.couriers, path: change.path ?? z.path };
  const why = zoneProblem(next);
  if (why) return toast(`Can’t change ${z.name}: ${why}`);
  now({
    write: async () => {
      await write.saveZone(write.zoneRef(m.business!.id, id), zoneFields({ businessId: m.business!.id, ...next }, false), false);
      track(kind, { corners: next.path.length, couriers: next.couriers.length });
    },
    failed: `Couldn’t change ${z.name}. Check the connection and try again.`,
    done,
  });
}

export function renameZone(id: string, name: string) {
  const z = model().zones.find((x) => x.id === id);
  if (!z || !name.trim() || name.trim() === z.name) return;
  changeZone(id, { name: name.trim() }, `Renamed ${name.trim()}`, 'zone_renamed');
}

/** A courier on or off a zone: the first one on gives the zone its colour. */
export function toggleZoneCourier(id: string, uid: string) {
  const m = model();
  const z = m.zones.find((x) => x.id === id);
  if (!z) return;
  const on = z.couriers.includes(uid);
  const couriers = on ? z.couriers.filter((u) => u !== uid) : [...z.couriers, uid];
  const who = m.person(uid)?.first ?? 'They';
  changeZone(id, { couriers }, on ? `${who} is off ${z.name}${couriers.length ? '' : ': it doesn’t limit anyone now'}` : `${who} covers ${z.name}`, 'zone_couriers_changed');
}

/** The shape the owner dragged it to. */
export function reshapeZone(id: string, path: ZoneSpot[]) {
  const z = model().zones.find((x) => x.id === id);
  if (!z) return;
  ui.set({ reshaping: false });
  if (path.length === z.path.length && path.every((p, i) => Math.abs(p.lat - z.path[i].lat) < 1e-7 && Math.abs(p.lng - z.path[i].lng) < 1e-7)) return;
  changeZone(id, { path }, `${z.name}’s new shape saved`, 'zone_reshaped');
}

/** Deleted after 5 s with Undo: what the owner drew can't be brought back once it's gone. */
export function deleteZone(id: string) {
  const m = model();
  const z = m.zones.find((x) => x.id === id);
  if (!z) return;
  pendZone(id, null);
  if (ui.get().zone === id) ui.set({ zone: null, reshaping: false, renaming: false });
  hold(`${z.name} deleted`, {
    send: async () => {
      await write.deleteZone(m.business!.id, id);
      unpendZone(id);
      track('zone_deleted', { couriers: z.couriers.length });
    },
    undo: () => unpendZone(id),
    failed: `Couldn’t delete ${z.name}. Check the connection and try again.`,
  });
}

// ── Sharing stops out across couriers (src/app/sharing.ts) ──────────────

/**
 * Sends shared-out routes, each as a normal sent route (`write.sendRoute`, with `fromWaiting`): shown waiting at once,
 * written after 5 s with Undo. Real order and times come now: Google's Sort, through Routr's server, in batches of 25,
 * unless Sort all already asked for this very route. Stops Google placed more than a day ago are found again first; one
 * that can't be found stays waiting. Each route is its own transaction: one whose stops another tab sent first isn't
 * written, and the others still go.
 */
export const sendShared = (uids?: string[]) => once(() => sendSharedNow(uids));

async function sendSharedNow(uids?: string[]) {
  const view = sharedNow.get().view;
  const share = ui.get().share;
  if (!view || !share) return;
  const picked = view.routes.filter((r) => r.ids.length && (!uids || uids.includes(r.uid)));
  if (!picked.length) return toast('No route to send: pick who takes them first');
  const m0 = model();
  const got = await placeWaiting(m0, picked.flatMap((r) => r.ids));
  if (!got) return;
  const { ps, missing, why } = got;
  const m = model();
  const uid = m.business!.id;
  const byId = new Map(ps.map((p) => [p.id, p]));
  const start = m.shop ? { label: m.shop.label, lat: m.shop.lat, lng: m.shop.lng } : null;
  const names = new Map<string, string>();
  const plans = picked.flatMap((r) => {
    const stops = r.ids.map((id) => byId.get(id)).filter((x): x is PlanStop => !!x);
    if (!stops.length) return [];
    const stored = new Map(stops.map((x) => [x.id, planStopToStored(x)]));
    const sorted = share.sorted[r.uid];
    const sameOrder = !!sorted && sorted.order.length === stops.length && sorted.order.every((id) => stored.has(id));
    // Each courier's route gets the next name of the day for them ("Afternoon run", "Afternoon run 2").
    const name = runName(m, r.uid);
    names.set(r.uid, name);
    const until = stops.map((x) => x.googleUntil).filter((t): t is number => t != null);
    const waitingIds = stops.map((x) => x.waitingId!).filter(Boolean);
    const ref = write.newRouteRef();
    const shownTiming = sameOrder ? sorted! : straight(start, r.ids.filter((id) => stored.has(id)).map((id) => pointOf(stored.get(id)!)));
    const shown = write.sendFields({ businessId: uid, riderId: r.uid, stops: shownTiming.order.map((id) => stored.get(id)!), optimization: optimizationJson(shownTiming, !!start), name, start, googleUntil: until.length ? Math.min(...until) : null });
    return [{ r, stops, stored, sorted: sameOrder ? sorted! : null, name, until, waitingIds, ref, shown, fromWaiting: fromWaitingOf(m, waitingIds) }];
  });
  if (!plans.length) return toast(why, { ms: 6000 });
  for (const x of plans) pendRoute(x.ref.id, { ...x.shown, sentAt: Date.now() });
  const sentIds = plans.flatMap((x) => x.waitingIds);
  pendWaiting(sentIds, () => null);
  const before = share;
  // What's sent leaves the share; the rest stays to send later.
  const rest = before.ids.filter((id) => !sentIds.includes(id));
  ui.set(rest.length ? { share: { ...before, ids: rest, sorted: Object.fromEntries(Object.entries(before.sorted).filter(([u]) => !plans.some((x) => x.r.uid === u))) } } : { share: null, view: 'day', sel: new Set() });
  const n = plans.reduce((a, x) => a + x.stops.length, 0);
  const unpend = (xs: typeof plans) => {
    for (const x of xs) unpendRoute(x.ref.id);
    unpendWaiting(xs.flatMap((x) => x.waitingIds));
  };
  hold(plans.length === 1 ? `Sending to ${m.person(plans[0].r.uid)?.first ?? 'them'} · ${plural(n, 'stop')}` : `Sending ${plural(plans.length, 'route')} · ${plural(n, 'stop')}`, {
    send: async () => {
      const from = start ?? (await placeStart(m));
      const done = await Promise.allSettled(
        plans.map(async (x) => {
          const t =
            x.sorted ??
            (await sortStops(
              from,
              x.r.ids.filter((id) => x.stored.has(id)).map((id) => ({ ...pointOf(x.stored.get(id)!), hint: 'auto' as Hint, pickupId: (x.stored.get(id)!.pickupId as string | null | undefined) ?? null })),
              false,
            ));
          await write.sendRoute(x.ref, { businessId: uid, riderId: x.r.uid, stops: t.order.map((id) => x.stored.get(id)!), optimization: optimizationJson(t, !!from), name: x.name, start: from, googleUntil: x.until.length ? Math.min(...x.until) : null }, x.waitingIds, x.fromWaiting);
          return t;
        }),
      );
      const ok = plans.filter((_, i) => done[i].status === 'fulfilled');
      const bad = plans.filter((_, i) => done[i].status === 'rejected');
      unpend(plans);
      track('routes_shared', { routes: ok.length, failed: bad.length, stops: stopsBucket(ok.reduce((a, x) => a + x.stops.length, 0)), balance: before.balance, fewest: before.fewest, moved_by_hand: Object.keys(before.moved).length > 0 });
      if (!ok.length) throw done.find((d) => d.status === 'rejected')?.reason ?? new Error('not sent');
      const who = ok.map((x) => m.person(x.r.uid)?.first ?? 'a courier');
      const straightOnes = done.filter((d) => d.status === 'fulfilled' && d.value.method === 'haversine').length;
      say(
        [
          ok.length === 1 ? `Sent to ${who[0]}. It waits on their map until they start it.` : `Sent to ${who.slice(0, -1).join(', ')} and ${who.at(-1)}. Each waits on their map until they start it.`,
          straightOnes ? `${plural(straightOnes, 'route')} timed by straight lines: Google didn’t answer.` : '',
          bad.length ? `${bad.map((x) => m.person(x.r.uid)?.first ?? 'One').join(' and ')}’s wasn’t sent: some of its stops had just been sent or removed (another tab?). They’re still in Waiting.` : '',
        ]
          .filter(Boolean)
          .join(' '),
        8000,
      );
    },
    undo: () => {
      unpend(plans);
      ui.set({ share: before, view: 'share', page: 'live', map: true });
    },
    failed: (e) => (e instanceof write.WaitingGone ? 'Nothing was sent: those stops have just been sent or removed (another tab?). Check Waiting.' : 'Couldn’t send them. Check the connection and try again: they’re still in Waiting.'),
  });
  if (missing.length) say(stayWaiting(why, missing.length), 6000);
}

/**
 * Sort all: Google orders and times each shared-out route through Routr's server (one request per 25 stops), so the
 * numbers shown are real before sending. Only when the owner asks: each try at sharing out asks Google for nothing.
 */
export async function sortShared() {
  const view = sharedNow.get().view;
  if (!view) return;
  const m = model();
  const start = m.shop ? { lat: m.shop.lat, lng: m.shop.lng } : null;
  const todo = view.routes.filter((r) => r.ids.length > 1);
  if (!todo.length) return;
  toast(`Sorting ${plural(todo.length, 'route')} with Google…`, { ms: 60_000 });
  const pickup = (id: string) => {
    const w = view.stops.find((x) => x.id === id);
    return typeof w?.stop.pickupId === 'string' ? w.stop.pickupId : null;
  };
  const out = await Promise.all(todo.map(async (r) => [r.uid, await sortStops(start, r.ids.map((id) => ({ id, ...view.at.get(id)!, hint: 'auto' as Hint, pickupId: pickup(id) })), false)] as const));
  setShare((x) => ({ sorted: { ...x.sorted, ...Object.fromEntries(out) } }));
  const straightOnes = out.filter(([, t]) => t.method === 'haversine');
  track('routes_shared_sorted', { routes: out.length, timed: straightOnes.length ? 'haversine' : 'routesApi' });
  toast(straightOnes.length ? `Sorted, ${plural(straightOnes.length, 'route')} by straight lines: ${straightOnes[0][1].why?.toLowerCase() ?? 'Google didn’t answer'}` : `${plural(out.length, 'route')} in Google’s order, with its times`, { ms: 6000 });
}

/**
 * Moves stops by hand while sharing out: onto [uid]'s route, where each fits best. A stop in a zone that courier doesn't
 * cover still moves (a hand move is on purpose), and the toast says so.
 */
export function moveShared(ids: string[], uid: string) {
  const view = sharedNow.get().view;
  const m = model();
  if (!view || !ids.length) return;
  const warn = ids.map((id) => (view.at.get(id) ? zoneWarning(m, view.at.get(id)!, uid) : null)).filter(Boolean);
  setShare((x) => ({ moved: { ...x.moved, ...Object.fromEntries(ids.map((id) => [id, uid])) }, open: uid }));
  ui.set({ sel: new Set() });
  const who = m.person(uid)?.first ?? 'them';
  track('shared_stops_moved', { stops: stopsBucket(ids.length), against_zone: warn.length > 0 });
  toast(warn.length ? `${plural(ids.length, 'stop')} to ${who} · ${warn[0]}: kept, as you moved ${ids.length === 1 ? 'it' : 'them'}` : `${plural(ids.length, 'stop')} to ${who}`, { ms: warn.length ? 6000 : undefined });
}

/** Waiting stops found on the map again (their place ID, else their address), so they can be shared out or planned. */
export const findWaitingAgain = (ids: string[]) =>
  once(async () => {
    const m = model();
    const got = await placeWaiting(m, ids);
    if (!got) return;
    if (got.missing.length) say(stayWaiting(got.why, got.missing.length), 6000);
    else toast(ids.length === 1 ? 'Found on the map' : `${plural(ids.length, 'stop')} found on the map`);
  });


// ── A courier's profile (Couriers → their record) ─────────────────────────

/** A courier's vehicle or pay being changed: shown at once, written after its 5 seconds. */
export const profileEdits = createStore<{ edits: Record<string, { vehicle?: string; onCall?: boolean }> }>({ edits: {} });

/** Sets [k] to [v], or, with [v] undefined, takes the change off when it's still [was] (a newer one may be waiting). */
function editProfile(uid: string, k: 'vehicle' | 'onCall', v: string | boolean | undefined, was?: string | boolean) {
  profileEdits.set((s) => {
    const mine: Record<string, unknown> = { ...s.edits[uid] };
    if (v !== undefined) mine[k] = v;
    else if (mine[k] === was) delete mine[k];
    return { edits: { ...s.edits, [uid]: mine } };
  });
}

/** A courier's vehicle and pay as shown: the change waiting, else as kept. */
export const vehicleNow = (p: Person, e = profileEdits.get().edits) => e[p.uid]?.vehicle ?? p.courier.vehicle;
export const onCallNow = (p: Person, e = profileEdits.get().edits) => e[p.uid]?.onCall ?? p.onCall;

/** The courier's vehicle: what they carry when stops are shared out and their marker go by it. */
export function changeVehicle(uid: string, vehicle: string) {
  const p = model().person(uid);
  if (!p || vehicleNow(p) === vehicle) return;
  editProfile(uid, 'vehicle', vehicle);
  hold(`${p.first}’s vehicle is a ${vehicleWords(vehicle).toLowerCase()} now`, {
    send: async () => {
      await write.setCourierVehicle(uid, vehicle);
      editProfile(uid, 'vehicle', undefined, vehicle);
      track('live_action', { action: 'courier_vehicle_changed', vehicle });
    },
    undo: () => editProfile(uid, 'vehicle', undefined, vehicle),
  });
}

/** On call (keeps the delivery fees) or employed (hands in everything taken), as the phone's payment settings have it. */
export function changeOnCall(uid: string, onCall: boolean) {
  const m = model();
  const p = m.person(uid);
  if (!p || !m.business || onCallNow(p) === onCall) return;
  const businessId = m.business.id;
  editProfile(uid, 'onCall', onCall);
  hold(onCall ? `${p.first} is on call now · keeps the delivery fees` : `${p.first} is employed now · hands in everything taken`, {
    send: async () => {
      await write.setCourierPay(businessId, uid, onCall);
      editProfile(uid, 'onCall', undefined, onCall);
      track('live_action', { action: 'courier_pay_changed', on_call: onCall });
    },
    undo: () => editProfile(uid, 'onCall', undefined, onCall),
  });
}
